import { useSnackbar } from 'notistack'
import React, { useEffect, useState, useRef } from 'react'

import levenshtein from 'fast-levenshtein'
import Divider from '@/components/Divider'
import Pagination from '@/components/Table/TablePagination'
import { useFetch } from '@/hooks/useFetch'
import PdfService from '@/pdf/PdfService'
import ApiService from '@/services/Api'
import { StreamingPartner } from '@/types/streamingPartner'
import { streamingPartners } from '@/utils/streamingPartners'
import {
    EditOutlined as EditIcon,
    AspectRatioOutlined as AccessCompanyIcon,
    DeleteOutlineOutlined as DeleteIcon,
    PictureAsPdfOutlined as PdfIcon,
    PeopleOutlined as PeopleIcon,
    ContentCopyOutlined as ContentCopyIcon,
    CheckOutlined as CheckIcon,
    GroupAddOutlined as AddUserIcon,
    PersonRemoveOutlined as RemoveUserIcon
} from '@mui/icons-material'
import WifiTetheringIcon from '@mui/icons-material/WifiTethering'
import {
    Grid,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Paper,
    Button,
    TextField,
    Dialog,
    DialogTitle,
    DialogActions,
    CircularProgress,
    IconButton,
    FormControl,
    MenuItem,
    Select,
    InputLabel,
    Checkbox,
    FormControlLabel,
    Link,
    Box,
    useTheme,
    Chip,
    DialogContent,
    Typography,
    Autocomplete
} from '@mui/material'

import useStyles from './styles'
import PasswordField from '@/components/PasswordField/PasswordField'
import { subDays, differenceInDays, startOfDay, endOfDay } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import CompanyFormDialog from '@/components/CompanyInfo/CompanyFormDialog'

export type ProductType = 'codec' | 'metrics' | 'intranet'
export type CodecRoleType = 'studio' | 'communicator'

export const INTRANET_ROLES = [
    'administrator',
    'management',
    'opec',
    'financial',
    'commercial',
    'artistic',
    'host',
    'other'
] as const

export type IntranetRoleType = (typeof INTRANET_ROLES)[number] | 'admin'

export const INTRANET_ROLE_LABELS: Record<IntranetRoleType, string> = {
    administrator: 'Administrador',
    management: 'Gestão',
    opec: 'OPEC',
    financial: 'Financeiro',
    commercial: 'Comercial',
    artistic: 'Artístico',
    host: 'Locutor',
    other: 'Outro',
    admin: 'Administrador (legado)'
}

export type UserType = {
    uuid: string
    companyUuid: string
    name: string
    email: string
    password: string
    product: ProductType
    role: CodecRoleType | IntranetRoleType
    isBetaTester: boolean
    createdAt: Date
    updatedAt: Date
}

type IcecastType = {
    uuid?: string;
    companyUuid?: string;
    streamId?: number;
    currentBitrate?: number;
    currentFrequency?: number;
    currentFormat?: string;
    currentCodec?: string;
    dockerImage?: string;
    streamServerURL?: string;
    streamServerPort?: string;
    mountpointUsername?: string;
    mountpointPassword?: string;
    mountpointPort?: string;
    mountpointName?: string;
    adminUsername?: string;
    adminPassword?: string;
    streamingPartner?: StreamingPartner
    createdAt?: Date;
    updatedAt?: Date;
};

export type NoticeType = {
    uuid?: string
    userUuid?: string
    message?: string
    startDate?: Date | undefined
    endDate?: Date | undefined
    showAs?: string
    status?: boolean | unknown
    createdAt?: Date
    updatedAt?: Date
}

export type AddressType = {
    uuid?: string
    country?: string
    state?: string
    city?: string
    neighborhood?: string
    street?: string
    number?: string
    complement?: string
    latitude?: string | null
    longitude?: string | null
    zipcode?: string
}

export type CompanyType = {
    whatsappCommercial?: string | undefined
    campaignDialMonthLimit?: number | null
    uuid?: string
    streamId?: number
    addressUuid?: string

    frequency?: string
    externalIcecastUrl?: string

    name?: string
    corporateName?: string
    cnpjCpf?: string
    fistel?: string
    email?: string
    payerEmail?: string
    phoneNumber?: string
    payerPhoneNumber?: string
    logoBase64?: string | null
    logoUrl?: string
    site?: string;
    twitterLink?: string;
    instagramLink?: string;
    facebookLink?: string;
    youtubeLink?: string;
    publicWhatsappPhoneNumber?: string;
    aboutContent?: string
    mobileAppUuid?: string;
    urlRadiosComBr?: string

    streamPassword?: string

    createdAt?: Date
    updatedAt?: Date
    deletedAt?: Date
    trialExpireAt?: Date

    whatsappEnabled?: boolean
    whatsappHasSuspended?: boolean
    concurrenceEnabled?: boolean
    enabled?: boolean

    anydeskPassword?: string
    anydeskCode?: string
    supportPhonenumber?: string

    address?: AddressType
    users?: UserType[]
    icecast?: IcecastType
    notices?: NoticeType[]
    segments?: string[]

    streamingServerUrl?: string

    coveredCities?: string[]
    mapsZoom?: number
    status?: 'trial' | 'internal' | 'customer' | 'concurrence' | 'expired' | 'deleted'
    plan?: 'basic' | 'free' | 'full'

    rhRadioUuid?: string
    rhRecorderUuid?: string

    adsolutionIds?: string[] | null

    referralPartner?: 'ACERT' | 'ADR' | 'Loopert'
    cnpjBilling?: string
    emailOpec?: string
    emailCommercial?: string
    whatsappOpec?: string

    adrCoreDisabled?: boolean

    addressJson?: {
        state?: string
        city?: string
        neighborhood?: string
        street?: string
        number?: string
        complement?: string
        latitude?: string | null
        longitude?: string | null
    }
}

const similarity = (a: string, b: string): number => {
    if (!a || !b) return 0
    a = a.toLowerCase().trim()
    b = b.toLowerCase().trim()
    const longer = Math.max(a.length, b.length)
    if (longer === 0) return 1
    return (longer - levenshtein.get(a, b)) / longer
}

const getSimilarityThreshold = (name: string): number => {
    return (name?.length || 0) >= 10 ? 0.75 : 0.50
}

const Companies: React.FC = () => {
    const classes = useStyles()
    const theme = useTheme()
    const { enqueueSnackbar } = useSnackbar()

    const [companies, setCompanies] = useState<CompanyType[]>([])
    const [company, setCompany] = useState<CompanyType>({})
    const [icecast, setIcecast] = useState<IcecastType>()
    const [companyUuid, setCompanyUuid] = useState<string>('')
    const [companyName, setCompanyName] = useState<string>('')
    const [openedCompanyDialog, setOpenedCompanyDialog] = useState<boolean>(false)
    const [openedIcecastDialog, setOpenedIcecastDialog] = useState<boolean>(false)
    const [openedCompanyDelete, setOpenedCompanyDelete] = useState<boolean>(false)
    const [loadingCompany, setLoadingCompany] = useState<boolean>(false)
    const [deletingCompany, setDeletingCompany] = useState<boolean>(false)
    const [searchInput, setSearchInput] = useState<string>('')
    const [filterEnabled, setFilterEnabledInput] = useState<boolean>(false)
    const [filterType, setFilterType] = useState<'clients' | 'competitors' | undefined>(undefined)
    const [selectedPartner, setSelectedPartner] = useState('')
    const [selectedState, setSelectedState] = useState('')
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(10)
    const [openedAudioInfoDialog, setOpenedAudioInfoDialog] = useState<boolean>(false)
    const [detectedAudioInfo, setDetectedAudioInfo] = useState<any>(null)
    const [loadingAudioInfo, setLoadingAudioInfo] = useState<boolean>(false)
    const [loadingReportPdf, setLoadingReportPdf] = useState<boolean>(false)
    const [openDuplicateWarning, setOpenDuplicateWarning] = useState(false)
    const [duplicateCompany, setDuplicateCompany] = useState<CompanyType[]>([])
    const [openedUsersDialog, setOpenedUsersDialog] = useState(false)
    const [selectedCompanyUsers, setSelectedCompanyUsers] = useState<UserType[]>([])
    const [copiedEmail, setCopiedEmail] = useState<string | null>(null)
    const [openedManageUsersDialog, setOpenedManageUsersDialog] = useState(false)
    const [manageCompanyUuid, setManageCompanyUuid] = useState('')
    const [manageCompanyName, setManageCompanyName] = useState('')
    const [manageCompanyUsers, setManageCompanyUsers] = useState<any[]>([])
    const [manageAvailableUsers, setManageAvailableUsers] = useState<any[]>([])
    const [manageSelectedUser, setManageSelectedUser] = useState<any | null>(null)
    const [loadingManageUsers, setLoadingManageUsers] = useState(false)
    const [savingManageUser, setSavingManageUser] = useState<string | null>(null)
    const [removingUsers, setRemovingUsers] = useState<string[]>([])
    const [manageUsersSearch, setManageUsersSearch] = useState('')
    const timeoutRef = useRef<NodeJS.Timeout | null>(null)
    const ignoreDuplicateCheckRef = useRef(false)
    const VALID_FORMATS = ['aac_low', 'aac_he', 'aac_he_v2'];
    const VALID_BITRATES = [32, 40, 48, 56, 64, 80, 96, 112, 128];
    const VALID_FREQUENCIES = [44100, 48000];

    const isValidFormat = (format: string) => VALID_FORMATS.includes(format);
    const isValidBitrate = (bitrate: number) => VALID_BITRATES.includes(bitrate);
    const isValidFrequency = (frequency: number) => VALID_FREQUENCIES.includes(frequency);

    const [filterTrial, setFilterTrial] = useState<boolean>(false)
    const [filterExpired, setFilterExpired] = useState<boolean>(false)

    const fetchAudioInfo = async (): Promise<void> => {
        if (!icecast?.streamServerURL || !icecast?.streamServerPort || !icecast?.mountpointName) {
            enqueueSnackbar('URL do servidor, porta e mount point são obrigatórios para buscar dados do áudio.', { variant: 'warning' })
            return
        }

        try {
            setLoadingAudioInfo(true)
            const { data } = await ApiService.post('/icecast/audio-info', {
                stream_server_url: icecast.streamServerURL,
                server_port: icecast.streamServerPort,
                mount_point: icecast.mountpointName
            })

            setDetectedAudioInfo(data.audioInfo)
            setOpenedAudioInfoDialog(true)
        } catch (error: any) {
            const message = error.data?.message || `Não foi possível obter dados do stream ${icecast.streamServerURL}:${icecast.streamServerPort}${icecast.mountpointName}`
            enqueueSnackbar(message, { variant: 'error' })
        } finally {
            setLoadingAudioInfo(false)
        }
    }

    const applyDetectedAudioInfo = () => {
        if (detectedAudioInfo) {
            const filteredData: any = {};
            const skippedFields: string[] = [];

            if (isValidBitrate(detectedAudioInfo.bitrate)) {
                filteredData.currentBitrate = detectedAudioInfo.bitrate;
            } else {
                skippedFields.push('Bitrate');
            }

            if (isValidFrequency(detectedAudioInfo.frequency)) {
                filteredData.currentFrequency = detectedAudioInfo.frequency;
            } else {
                skippedFields.push('Frequência');
            }

            if (isValidFormat(detectedAudioInfo.format)) {
                filteredData.currentFormat = detectedAudioInfo.format;
            } else {
                skippedFields.push('Formato');
            }

            filteredData.currentCodec = detectedAudioInfo.codec;

            setIcecast({
                ...icecast,
                ...filteredData
            });

            if (skippedFields.length === 0) {
                enqueueSnackbar('Todos os dados foram aplicados com sucesso!', { variant: 'success' });
            } else {
                enqueueSnackbar(
                    `Dados aplicados com sucesso! Os seguintes campos foram ignorados por não serem compatíveis: ${skippedFields.join(', ')}.`,
                    { variant: 'warning' }
                );
            }
        }
        setOpenedAudioInfoDialog(false);
    };

    const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
        setPage(newPage)
    }

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(0)
    }

    const normalizeText = (text: string) =>
        text.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, '')

    const filteredCompanies = companies.filter((company) => {
        if (!filterEnabled && !company?.enabled) return false

        if (filterType === 'competitors' && company.status !== 'concurrence') return false
        if (filterType === 'clients' && company.status !== 'customer') return false
        if (selectedPartner !== '' && company.icecast?.streamingPartner !== selectedPartner) return false
        if (selectedState !== '' && (company.addressJson?.state || company.address?.state) !== selectedState) return false
        if (filterTrial && company.status !== 'trial') return false
        if (filterExpired && company.status !== 'expired') return false

        if (searchInput) {
            const search = normalizeText(searchInput)
            const nameMatch = normalizeText(company.name || '').includes(search)
            const streamIdMatch = normalizeText(String(company.streamId) || '').includes(search)
            const cityMatch = normalizeText(company.addressJson?.city || company.address?.city || '').includes(search)

            return nameMatch || streamIdMatch || cityMatch
        }

        return true
    })

    const toggleFilterType = (type: 'clients' | 'competitors') => {
        setFilterType((prevType) => (prevType === type ? undefined : type))
    }

    const paginatedCompanies = rowsPerPage === -1
        ? filteredCompanies
        : filteredCompanies.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)

    const availableStates = Array.from(new Set(
        companies
            .map((company) => company.addressJson?.state || company.address?.state)
            .filter((state): state is string => !!state)
    )).sort()

    const formatMapping = {
        aac_low: 'AAC LOW/LC',
        aac_he: 'AAC HE',
        aac_he_v2: 'AAC HE V2',
        aac_lc: 'AAC LC'
    }

    const openCompanyDelete = (uuid: string, name: string) => {
        setCompanyUuid(uuid)
        setCompanyName(name)
        setOpenedCompanyDelete(true)
    }

    const openCompanyIcecast = (icecast?: IcecastType, company?: CompanyType) => {
        if (icecast) {
            setIcecast({
                ...icecast,
                companyUuid: company?.uuid ?? '',
                streamId: company?.streamId ?? 0,
                streamServerURL: icecast.streamServerURL ?? '',
                adminUsername: icecast.adminUsername ?? '',
                adminPassword: icecast.adminPassword ?? '',
                streamServerPort: icecast.streamServerPort ?? '',
                mountpointName: icecast.mountpointName ?? '',
                streamingPartner: icecast.streamingPartner ?? 'Loopert'
            })
        } else {
            setIcecast({
                uuid: '',
                companyUuid: company?.uuid ?? '',
                streamId: company?.streamId ?? 0,
                dockerImage: '',
                streamServerURL: '',
                streamServerPort: '',
                mountpointUsername: '',
                mountpointPassword: '',
                mountpointPort: '',
                mountpointName: '',
                adminUsername: '',
                adminPassword: '',
                streamingPartner: 'Loopert',
                createdAt: new Date(),
                updatedAt: new Date()
            })
        }

        setOpenedIcecastDialog(true)
    }

    const openCompanyDialog = async (company?: CompanyType) => {
        if (company?.uuid) {
            try {
                const { data } = await ApiService.get(`/company/${company.uuid}`)
                company = data
            } catch (error) {
                console.error('Erro ao carregar dados da empresa:', error)
                enqueueSnackbar('Erro ao carregar dados da empresa', { variant: 'error' })
            }
        }

        const initialAdsolutionIds = company?.adsolutionIds ?? []

        setCompany(company
            ? {
                ...company,
                whatsappHasSuspended: company.whatsappHasSuspended ?? false,
                trialExpireAt: company.trialExpireAt ? new Date(company.trialExpireAt) : undefined,
                adsolutionIds: initialAdsolutionIds
            }
            : {
                whatsappEnabled: true,
                whatsappHasSuspended: false,
                enabled: false,
                address: {
                    state: '',
                    city: '',
                    neighborhood: '',
                    street: '',
                    number: '',
                    complement: '',
                    zipcode: '',
                    latitude: null,
                    longitude: null,
                },
                coveredCities: [],
                adsolutionIds: []
            })
        setOpenedCompanyDialog(true)
    }

    const handleCloseUsersDialog = () => {
        setOpenedUsersDialog(false)
        setCopiedEmail(null)
        if (timeoutRef.current) clearTimeout(timeoutRef.current)
    }

    const userCompanyUuids = (user: any): string[] =>
        Array.isArray(user.companyUuids)
            ? user.companyUuids
            : user.companyUuid
                ? [user.companyUuid]
                : []

    const buildUserPayload = (user: any, companyUuids: string[]) => ({
        uuid: user.uuid,
        companyUuid: companyUuids[0] || '',
        companyUuids,
        name: user.name || '',
        email: user.email || '',
        product: user.product || 'metrics',
        role: user.role || 'admin',
        isBetaTester: !!user.isBetaTester,
        phoneNumber: user.phoneNumber ?? null,
        isReportsEnabled: !!user.isReportsEnabled,
        isAccountManager: !!user.isAccountManager,
        pictureKey: user.pictureKey ?? null,
        preferences: user.preferences ?? null,
        createdAt: user.createdAt || '',
        lastHeardAt: user.lastHeardAt ?? null,
        updatedAt: user.updatedAt || '',
        permissions: user.permissions ?? undefined
    })

    const fetchAllUsers = async (): Promise<any[]> => {
        try {
            const { data } = await ApiService.get('/user/list')
            return (data || []).filter((u: any) => u.deletedAt == null)
        } catch {
            return []
        }
    }

    const loadManageUsers = async (companyUuidValue: string) => {
        setLoadingManageUsers(true)
        const allUsers = await fetchAllUsers()
        const associated = allUsers.filter((u) =>
            userCompanyUuids(u).includes(companyUuidValue)
        )
        setManageCompanyUsers(associated)
        setManageAvailableUsers(allUsers.filter((u) => !associated.some((a) => a.uuid === u.uuid)))
        setManageSelectedUser(null)
        setLoadingManageUsers(false)
    }

    const openManageUsersDialog = async (company: CompanyType) => {
        const uuid = company?.uuid || ''
        setManageCompanyUuid(uuid)
        setManageCompanyName(company?.name || 'esta rádio')
        setManageUsersSearch('')
        setOpenedManageUsersDialog(true)
        await loadManageUsers(uuid)
    }

    const handleCloseManageUsersDialog = () => {
        setOpenedManageUsersDialog(false)
        setManageSelectedUser(null)
        setManageUsersSearch('')
    }

    const handleAddUserToCompany = async () => {
        if (!manageSelectedUser || !manageCompanyUuid) return
        setSavingManageUser(manageSelectedUser.uuid)
        try {
            const companyUuids = userCompanyUuids(manageSelectedUser)
            const updated = companyUuids.includes(manageCompanyUuid)
                ? companyUuids
                : [...companyUuids, manageCompanyUuid]
            await ApiService.post('/user', buildUserPayload(manageSelectedUser, updated))
            enqueueSnackbar('Usuário associado à rádio com sucesso.', { variant: 'success' })
            setManageCompanyUsers((prev) => prev.some((u) => u.uuid === manageSelectedUser.uuid) ? prev : [...prev, manageSelectedUser])
            setManageAvailableUsers((prev) => prev.filter((u) => u.uuid !== manageSelectedUser.uuid))
            setManageSelectedUser(null)
        } catch (error: any) {
            enqueueSnackbar(error?.data || 'Ocorreu um erro ao associar o usuário.', { variant: 'error' })
        } finally {
            setSavingManageUser(null)
        }
    }

    const handleRemoveUserFromCompany = async (user: any) => {
        if (!manageCompanyUuid) return
        setRemovingUsers((prev) => [...prev, user.uuid])
        try {
            const companyUuids = userCompanyUuids(user)
            const updated = companyUuids.filter((u) => u !== manageCompanyUuid)
            await ApiService.post('/user', buildUserPayload(user, updated))
            enqueueSnackbar('Usuário desvinculado da rádio com sucesso.', { variant: 'success' })
            setManageCompanyUsers((prev) => prev.filter((u) => u.uuid !== user.uuid))
            setManageAvailableUsers((prev) => prev.some((u) => u.uuid === user.uuid) ? prev : [...prev, user])
        } catch (error: any) {
            enqueueSnackbar(error?.data || 'Ocorreu um erro ao desvincular o usuário.', { variant: 'error' })
        } finally {
            setRemovingUsers((prev) => prev.filter((u) => u !== user.uuid))
        }
    }

    const filteredManageCompanyUsers = manageCompanyUsers.filter((user) => {
        if (!manageUsersSearch) return true
        const search = normalizeText(manageUsersSearch)
        return normalizeText(user.name || '').includes(search) || normalizeText(user.email || '').includes(search)
    })

    const handleCloseCompanyDialog = () => {
        setOpenedCompanyDialog(false)
    }

    const getCompanies = async () => {
        const { data } = await ApiService.get('/company')
        const normalizedData = (data || []).map((companyItem: CompanyType) => ({
            ...companyItem,
            adsolutionIds: companyItem.adsolutionIds ?? []
        }))
        setCompanies(normalizedData)
        if (normalizedData.length > 0) {
            setIcecast(normalizedData[0].icecast)
        }
    }

    const deleteCompany = async (companyUuid: string) => {
        setDeletingCompany(true)
        setCompanyUuid('')
        setCompanyName('')
        try {
            await ApiService.delete(`/company/${companyUuid}`)
            await getCompanies()

            setOpenedCompanyDelete(false)
            setDeletingCompany(false)
        } catch (error) {
            enqueueSnackbar(String(error || 'Ocorreu um erro ao tentar confirmar o codigo.'), { variant: 'error' })
            setOpenedCompanyDelete(false)
            setDeletingCompany(false)
        }
    }

    const saveCompany = async () => {
        if (!company?.coveredCities?.length) {
            enqueueSnackbar('É necessário selecionar ao menos uma cidade de atuação.', {
                variant: 'warning'
            })
            return
        }

        const validStatuses = ['trial', 'internal', 'customer', 'concurrence']
        if (!company?.status || !validStatuses.includes(company.status)) {
            enqueueSnackbar('É necessário selecionar um status válido para a empresa.', {
                variant: 'error'
            })
            return
        }

        if (company?.urlRadiosComBr) {
            const cleanedLink = company.urlRadiosComBr.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '')
            if (!cleanedLink.startsWith('radios.com.br')) {
                enqueueSnackbar('O campo "Link radios.com.br" deve começar com "radios.com.br".', {
                    variant: 'error'
                })
                return
            }
        }

        const duplicates = companies.filter(c =>
            c.uuid !== company?.uuid &&
            c.address?.city?.toLowerCase() === company?.address?.city?.toLowerCase() &&
            c.address?.state?.toLowerCase() === company?.address?.state?.toLowerCase() &&
            (c.name || company?.name) &&
            similarity(c.name || '', company?.name || '') >= getSimilarityThreshold(company?.name || '')
        ).slice(0, 8)

        if (duplicates.length > 0 && !ignoreDuplicateCheckRef.current) {
            setDuplicateCompany(duplicates)
            setOpenDuplicateWarning(true)
            return
        }

        try {
            const payload = {
                ...company,
                latitude: Number(company?.address?.latitude),
                longitude: Number(company?.address?.longitude),
                trialExpireAt: company?.trialExpireAt ? new Date(company.trialExpireAt).toISOString() : null
            };

            setLoadingCompany(true);
            await ApiService.post('/company', payload);

            handleCloseCompanyDialog();
        } catch (e) {
            let errorMessage = 'Ocorreu um erro ao criar a rádio.'

            if (e && typeof e === 'object' && 'data' in e) {
                const errorData = (e as any).data
                if (typeof errorData === 'string') {
                    errorMessage = errorData
                }
                else if (errorData && errorData.message) {
                    errorMessage = errorData.message
                }
            }

            enqueueSnackbar(errorMessage, {
                variant: 'error'
            })
        }

        getCompanies();
        setLoadingCompany(false);
    }

    const { isValidating } = useFetch<CompanyType[]>(
        '/company',
        (data: CompanyType[]) => {
            setCompanies(data)
        }
    )
    const saveOrUpdateIcecast = async (): Promise<void> => {
        if (!icecast) {
            enqueueSnackbar('Nenhum dado de Icecast fornecido.', { variant: 'error' })
            return
        }

        const requiredFields: { key: keyof typeof icecast, label: string }[] = [
            { key: 'streamServerURL', label: 'Stream Server URL' },
            { key: 'adminUsername', label: 'Admin Username' },
            { key: 'adminPassword', label: 'Admin Password' },
            { key: 'streamServerPort', label: 'Stream Server Port' },
            { key: 'mountpointName', label: 'Mountpoint Name' },
            { key: 'streamingPartner', label: 'Streaming Partner' }
        ]

        for (const field of requiredFields) {
            const value = icecast[field.key]
            if (value === undefined || value === null || value === '') {
                enqueueSnackbar(`O campo ${field.label} é obrigatório e não pode estar vazio.`, { variant: 'error' })
                return
            }
        }

        try {
            setLoadingCompany(true)
            await ApiService.post('/icecast', icecast)
            enqueueSnackbar('Icecast salvo com sucesso!', { variant: 'success' })
            getCompanies()
            setOpenedIcecastDialog(false)
        } catch (e: any) {
            const status = e.status;
            const message = e.data?.message || 'Ocorreu um erro ao salvar o Icecast';

            if (status === 409) {
                enqueueSnackbar(message, { variant: 'error' });
                return;
            }
            enqueueSnackbar('Ocorreu um erro ao salvar o Icecast', { variant: 'error' });
        } finally {
            setLoadingCompany(false)
        }
    }

    const [showSelection, setShowSelection] = useState(false);
    const [dateRange, setDateRange] = useState({
        start: subDays(new Date(), 7),
        end: subDays(new Date(), 1)
    });
    const executeReportGeneration = async () => {
        const daysDiff = differenceInDays(dateRange.end, dateRange.start) + 1;

        if (daysDiff > 8 || daysDiff <= 0) {
            enqueueSnackbar('O intervalo deve ser de 1 a 8 dias.', { variant: 'error' });
            return;
        }

        setShowSelection(false);
        setLoadingReportPdf(true);

        try {
            const params = {
                from: startOfDay(dateRange.start).toISOString(),
                to: endOfDay(dateRange.end).toISOString()
            };

            await PdfService.generateCoverAndOpen(companyUuid, params);
            enqueueSnackbar('Relatório gerado com sucesso!', { variant: 'success' });
        } catch (error: any) {
            const errorMessage = error?.message || 'Erro ao gerar PDF';
            enqueueSnackbar(errorMessage, {
                variant: error?.message.includes('Não há dados') ? 'warning' : 'error'
            });
        } finally {
            setLoadingReportPdf(false);
        }
    }

    return (
        <>
            <Grid container justifyContent="space-between">
                <Grid>
                    <TextField
                        id="company_search_field"
                        label="Buscar empresa" size="small" color="primary" variant="outlined"
                        value={searchInput}
                        onChange={({ target }) => setSearchInput(target.value)}
                    />

                    <FormControlLabel
                        className={classes.filterEnabledCheckbox}
                        control={
                            <Checkbox
                                id="filter_enabled_checkbox"
                                checked={filterEnabled}
                                onChange={({ target }) => setFilterEnabledInput(target.checked)}
                                color="default"
                            />
                        }
                        label={'Filtrar inativos'}
                    />
                </Grid>
                <Button
                    id="create_radio_button"
                    color="primary"
                    variant="contained"
                    onClick={() => openCompanyDialog()}
                >
                    Criar nova rádio
                </Button>
            </Grid>

            <Divider size={3} />

            <Grid display="flex" gap={2} marginBottom={2} flexWrap="wrap">
                <Button
                    id="filter_all_button"
                    variant={filterType === undefined && selectedPartner === '' && !filterTrial && !filterExpired ? 'contained' : 'outlined'}
                    onClick={() => {
                        setFilterType(undefined)
                        setSelectedPartner('')
                        setSelectedState('')
                        setFilterTrial(false)
                        setFilterExpired(false)
                        setPage(0)
                    }}
                    size='small'
                >
                    Todos
                </Button>
                <Button
                    id="filter_competitors_button"
                    variant={filterType === 'competitors' ? 'contained' : 'outlined'}
                    onClick={() => { toggleFilterType('competitors'); setFilterTrial(false); setFilterExpired(false); setPage(0) }}
                    size='small'
                >
                    Concorrentes
                </Button>
                <Button
                    id="filter_clients_button"
                    variant={filterType === 'clients' ? 'contained' : 'outlined'}
                    onClick={() => { toggleFilterType('clients'); setFilterTrial(false); setFilterExpired(false); setPage(0) }}
                    size='small'
                >
                    Clientes
                </Button>
                <Button
                    id="filter_trial_button"
                    variant={filterTrial ? 'contained' : 'outlined'}
                    onClick={() => {
                        setFilterTrial(prev => !prev)
                        if (!filterTrial) {
                            setFilterType(undefined)
                            setSelectedPartner('')
                            setSelectedState('')
                            setFilterExpired(false)
                        }
                        setPage(0)
                    }}
                    size='small'
                >
                    Período de Avaliação
                </Button>

                <Button
                    id="filter_expired_button"
                    variant={filterExpired ? 'contained' : 'outlined'}
                    onClick={() => {
                        setFilterExpired(prev => !prev)
                        if (!filterExpired) {
                            setFilterTrial(false)
                            setFilterType(undefined)
                            setSelectedPartner('')
                            setSelectedState('')
                        }
                        setPage(0)
                    }}
                    size='small'
                >
                    Expirados
                </Button>

                <FormControl variant="outlined" className={classes.formControlSelect} size='small'>
                    <InputLabel id="streaming_partner_filter_label">Parceiro de streaming</InputLabel>
                    <Select
                        id="streaming_partner_filter_field"
                        value={selectedPartner}
                        defaultValue=''
                        onChange={(e) => {
                            setSelectedPartner(e.target.value); setFilterTrial(false); setPage(0)
                        }
                        }
                        variant="outlined"
                        color="primary"
                        label="Parceiro de streaming"
                        labelId="streaming_partner_filter_label"
                    >
                        <MenuItem value=''>Todos</MenuItem>
                        {streamingPartners.map((partner) => (
                            <MenuItem key={partner.value} value={partner.value}>{partner.label}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl variant="outlined" className={classes.formControlSelect} size='small'>
                    <InputLabel id="state_filter_label">Estado</InputLabel>
                    <Select
                        id="state_filter_field"
                        value={selectedState}
                        defaultValue=''
                        onChange={(e) => {
                            setSelectedState(e.target.value); setFilterTrial(false); setPage(0)
                        }
                        }
                        variant="outlined"
                        color="primary"
                        label="Estado"
                        labelId="state_filter_label"
                    >
                        <MenuItem value=''>Todos</MenuItem>
                        {availableStates.map((state) => (
                            <MenuItem key={state} value={state}>{state}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

            </Grid>
            <Grid container>
                <TableContainer component={Paper} className={classes.table}>
                    <Table aria-label="simple table">
                        <TableHead>
                            <TableRow>
                                <TableCell>Nome</TableCell>
                                <TableCell align="center">Cidade</TableCell>
                                <TableCell align="center">Frequência</TableCell>
                                <TableCell align="center">ID do adstream/icecast</TableCell>
                                <TableCell align="center">Bitrate Icecast</TableCell>
                                <TableCell align="center">Frequência Icecast</TableCell>
                                <TableCell align="center">Format</TableCell>
                                <TableCell align="center">Status</TableCell>
                                <TableCell align="center">Ações</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {isValidating && companies.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={9} align="center" sx={{ padding: 4 }}>
                                        <CircularProgress size={32} />
                                    </TableCell>
                                </TableRow>
                            ) : (
                                paginatedCompanies.map((company) => (
                                    <TableRow key={company.uuid}>
                                        <TableCell component="th" scope="row" className={classes.truncateCell}>{company.name}</TableCell>
                                        <TableCell align="center">{company?.addressJson?.city || company?.address?.city}</TableCell>
                                        <TableCell align="center">{company?.frequency}</TableCell>
                                        <TableCell align="center">{company?.streamId}</TableCell>
                                        <TableCell align="center">{company?.icecast?.currentBitrate && `${company?.icecast?.currentBitrate}k`}</TableCell>
                                        <TableCell align="center">{company?.icecast?.currentFrequency}</TableCell>
                                        <TableCell align="center">{formatMapping[company?.icecast?.currentFormat as keyof typeof formatMapping] || company?.icecast?.currentFormat || ''}</TableCell>
                                        <TableCell align="center">
                                            {(() => {
                                                if (company?.status === 'expired') {
                                                    return (
                                                        <span style={{ color: '#f44336', fontWeight: 'bold' }}>
                                                            Expirado
                                                        </span>
                                                    )
                                                }
                                                return company?.status === 'trial' ? 'Período de Avaliação' :
                                                    company?.status === 'internal' ? 'Interna' :
                                                        company?.status === 'customer' ? 'Pagante' :
                                                            company?.status === 'concurrence' ? 'Concorrência' :
                                                                'Não definido'
                                            })()}
                                        </TableCell>
                                        <TableCell align="center">
                                            <IconButton
                                                onClick={() => openCompanyIcecast(company?.icecast, company)}
                                                title={'Icecast'}
                                            >
                                                <WifiTetheringIcon />
                                            </IconButton>

                                            <IconButton
                                                onClick={() => openCompanyDelete(company?.uuid || '', company?.name || 'esta rádio')}
                                                title='Deletar'
                                            >
                                                <DeleteIcon />
                                            </IconButton>

                                            <IconButton
                                                onClick={() => openCompanyDialog(company)}
                                                title='Editar'
                                            >
                                                <EditIcon />
                                            </IconButton>
                                            <IconButton
                                                onClick={async () => {
                                                    try {
                                                        const response = await ApiService.get(`/company/${company.uuid}/users`)
                                                        setSelectedCompanyUsers(response.data || [])
                                                        setOpenedUsersDialog(true)
                                                    } catch (err) {
                                                        enqueueSnackbar('Erro ao buscar usuários', { variant: 'error' })
                                                    }
                                                }}
                                                title="Usuários"
                                            >
                                                <PeopleIcon />
                                            </IconButton>

                                            <IconButton
                                                onClick={() => openManageUsersDialog(company)}
                                                title="Gerenciar usuários"
                                            >
                                                <AddUserIcon />
                                            </IconButton>

                                            <IconButton
                                                onClick={() => {
                                                    setCompanyUuid(company?.uuid || '');
                                                    setShowSelection(true);
                                                }}
                                                title='Gerar Relatório'
                                            >
                                                <PdfIcon />
                                            </IconButton>
                                            <Link href={`/companies/${company.uuid}`}>
                                                <IconButton title='Acessar'>
                                                    <AccessCompanyIcon />
                                                </IconButton>
                                            </Link>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                    <Pagination
                        count={filteredCompanies.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </TableContainer>
            </Grid>

            <CompanyFormDialog
                open={openedCompanyDialog}
                company={company}
                setCompany={setCompany}
                loading={loadingCompany}
                onClose={handleCloseCompanyDialog}
                onSave={saveCompany}
            />

            <Dialog
                open={showSelection || loadingReportPdf}
                onClose={() => !loadingReportPdf && setShowSelection(false)}
            >
                {loadingReportPdf ? (
                    <Grid container direction="column" alignItems="center" style={{ padding: 40, gap: 16 }}>
                        <CircularProgress size={32} />
                        <Typography>Gerando relatório, aguarde...</Typography>
                    </Grid>
                ) : (
                    <>
                        <DialogTitle>Período do Relatório</DialogTitle>
                        <DialogContent>
                            <Typography variant="body2" color="textSecondary" gutterBottom>
                                Selecione um intervalo de no máximo 8 dias.
                            </Typography>
                            <LocalizationProvider
                                dateAdapter={AdapterDateFns}
                                adapterLocale={ptBR}
                            >
                                <Box
                                    mt={2}
                                    display="flex"
                                    flexDirection="row"
                                    gap={2}
                                    sx={{ backgroundColor: 'transparent' }}
                                >
                                    <DatePicker
                                        label="Início"
                                        value={dateRange.start}
                                        onChange={(newValue) => newValue && setDateRange(p => ({ ...p, start: newValue }))}
                                        maxDate={subDays(new Date(), 1)}
                                        slotProps={{
                                            popper: {
                                                sx: {
                                                    '& .MuiPaper-root': {
                                                        backgroundColor: '#ffffff'
                                                    }
                                                }
                                            },
                                            textField: {
                                                sx: { backgroundColor: '#ffffff' }
                                            }
                                        }}
                                    />
                                    <DatePicker
                                        label="Fim"
                                        value={dateRange.end}
                                        onChange={(newValue) => newValue && setDateRange(p => ({ ...p, end: newValue }))}
                                        minDate={dateRange.start}
                                        maxDate={subDays(new Date(), 1)}
                                        slotProps={{
                                            popper: {
                                                sx: {
                                                    '& .MuiPaper-root': {
                                                        backgroundColor: '#ffffff'
                                                    }
                                                }
                                            },
                                            textField: {
                                                sx: { backgroundColor: '#ffffff' }
                                            }
                                        }}
                                    />
                                </Box>
                            </LocalizationProvider>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setShowSelection(false)}>Cancelar</Button>
                            <Button variant="contained" onClick={executeReportGeneration}>
                                Gerar Relatório
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>

            <Dialog open={openedIcecastDialog} onClose={() => setOpenedIcecastDialog(false)}>
                <DialogTitle className={classes.dialogTitle}>
                    {!icecast?.uuid ? 'Cadastrando dados do ' : 'Editando dados do '} Icecast
                </DialogTitle>
                <Grid container className={classes.companyDialogContainer}>

                    <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                        <TextField
                            id="stream_server_url_field"
                            className={classes.itemInput}
                            label="Stream Server URL"
                            value={icecast?.streamServerURL}
                            onChange={({ target }) => setIcecast({ ...icecast, streamServerURL: target.value })}
                            size="small"
                            variant="outlined"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                        <TextField
                            id="stream_server_port_field"
                            className={classes.itemInput}
                            label="Stream Server Port"
                            value={icecast?.streamServerPort}
                            onChange={({ target }) => setIcecast({ ...icecast, streamServerPort: target.value })}
                            size="small"
                            variant="outlined"
                        />
                    </Grid>

                    <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                        <TextField
                            id="admin_username_field"
                            className={classes.itemInput}
                            label="Admin Username"
                            value={icecast?.adminUsername}
                            onChange={({ target }) => setIcecast({ ...icecast, adminUsername: target.value })}
                            size="small"
                            variant="outlined"
                            inputProps={{
                                'data-lpignore': 'true',
                                'data-1password': 'true',
                                'data-op-ignore': 'true'
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                        <PasswordField
                            value={icecast?.adminPassword}
                            onChange={({ target }) => setIcecast({ ...icecast, adminPassword: target.value })}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                        <TextField
                            id="mountpoint_name_field"
                            className={classes.itemInput}
                            label="Mountpoint Name"
                            value={icecast?.mountpointName}
                            onChange={({ target }) => setIcecast({ ...icecast, mountpointName: target.value })}
                            size="small"
                            variant="outlined"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                        <FormControl variant="outlined" className={classes.formControl} size='small'
                        >
                            <InputLabel id="streaming_partner_label">Parceiro de streaming</InputLabel>
                            <Select
                                id="streaming_partner_field"
                                value={icecast?.streamingPartner}
                                onChange={({ target }) => setIcecast({ ...icecast, streamingPartner: target.value as StreamingPartner })}
                                variant="outlined"
                                color="primary"
                                label="Parceiro de streaming"
                                labelId="streaming_partner_label"
                            >
                                {streamingPartners.map((partner) => (
                                    <MenuItem key={partner.value} value={partner.value}>{partner.label}</MenuItem>
                                ))}

                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                        <FormControl variant="outlined" className={classes.formControl} size='small'>
                            <InputLabel id="icecast_bitrate_label">Bitrate</InputLabel>
                            <Select
                                id="icecast_bitrate_field"
                                value={icecast?.currentBitrate ?? ''}
                                onChange={({ target }) => setIcecast({ ...icecast, currentBitrate: Number(target.value) })}
                                variant="outlined"
                                color="primary"
                                label="Bitrate"
                                labelId="icecast_bitrate_label"
                            >
                                <MenuItem key={'32'} value={'32'}>32k</MenuItem>
                                <MenuItem key={'40'} value={'40'}>40k</MenuItem>
                                <MenuItem key={'48'} value={'48'}>48k</MenuItem>
                                <MenuItem key={'56'} value={'56'}>56k</MenuItem>
                                <MenuItem key={'64'} value={'64'}>64k</MenuItem>
                                <MenuItem key={'80'} value={'80'}>80k</MenuItem>
                                <MenuItem key={'96'} value={'96'}>96k</MenuItem>
                                <MenuItem key={'112'} value={'112'}>112k</MenuItem>
                                <MenuItem key={'128'} value={'128'}>128k</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                        <FormControl variant="outlined" className={classes.formControl} size='small'>
                            <InputLabel id="icecast_audio_format_label">Formato do áudio</InputLabel>
                            <Select
                                id="icecast_audio_format_field"
                                value={icecast?.currentFormat ?? ''}
                                onChange={({ target }) => setIcecast({ ...icecast, currentFormat: String(target.value) })}
                                variant="outlined"
                                color="primary"
                                label="Formato do áudio"
                                labelId="icecast_audio_format_label"
                            >
                                <MenuItem key={'aac_low'} value={'aac_low'}>AAC LOW/LC</MenuItem>
                                <MenuItem key={'aac_he'} value={'aac_he'}>AAC HE</MenuItem>
                                <MenuItem key={'aac_he_v2'} value={'aac_he_v2'}>AAC HE V2</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                        <FormControl variant="outlined" className={classes.formControl} size='small'>
                            <InputLabel id="icecast_audio_frequency_label">Frequência do áudio</InputLabel>
                            <Select
                                id="icecast_audio_frequency_field"
                                value={icecast?.currentFrequency ?? ''}
                                onChange={({ target }) => setIcecast({ ...icecast, currentFrequency: Number(target.value) })}
                                variant="outlined"
                                color="primary"
                                label="Frequência do áudio"
                                labelId="icecast_audio_frequency_label"
                            >
                                <MenuItem key={'44100'} value={'44100'}>44100 Hz</MenuItem>
                                <MenuItem key={'48000'} value={'48000'}>48000 Hz</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={12} className={classes.dialogItemInput}>
                        <Button
                            id="fetch_audio_info_button"
                            onClick={fetchAudioInfo}
                            color="primary"
                            variant="outlined"
                            fullWidth
                            disabled={loadingAudioInfo || !icecast?.streamId}
                            startIcon={loadingAudioInfo && <CircularProgress size={16} />}
                        >
                            {loadingAudioInfo ? 'Buscando dados...' : 'Buscar dados do Icecast'}
                        </Button>
                    </Grid>
                </Grid>
                <Divider size={2} />
                <Grid container justifyContent="flex-end" alignItems="center" spacing={2} >
                    <Grid item>
                        <Button
                            id="icecast_dialog_cancel_button"
                            onClick={() => setOpenedIcecastDialog(false)}
                            color="primary"
                        >
                            Cancelar
                        </Button>
                    </Grid>
                    <Grid item>
                        <DialogActions>
                            <Button
                                id="icecast_dialog_save_button"
                                onClick={saveOrUpdateIcecast}
                                color="primary"
                                autoFocus
                                variant="contained"
                                disabled={loadingCompany}
                                endIcon={loadingCompany && (
                                    <CircularProgress size={20} color="inherit" />
                                )}
                            >
                                {icecast?.uuid ? 'Salvar Alterações' : 'Cadastrar'}
                            </Button>
                        </DialogActions>
                    </Grid>
                </Grid>
            </Dialog>


            <Dialog open={openedAudioInfoDialog} onClose={() => setOpenedAudioInfoDialog(false)}>
                <DialogTitle className={classes.dialogTitle}>
                    Dados detectados do stream
                </DialogTitle>
                <Grid container className={classes.companyDialogContainer}>
                    {detectedAudioInfo && (
                        <>
                            <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                                <TextField
                                    label="Formato detectado"
                                    value={
                                        detectedAudioInfo.format ?
                                            (isValidFormat(detectedAudioInfo.format)
                                                ? (formatMapping[detectedAudioInfo.format as keyof typeof formatMapping] || detectedAudioInfo.format)
                                                : 'Desconhecido (incompatível)'
                                            ) : 'Desconhecido'
                                    }
                                    size="small"
                                    variant="outlined"
                                    InputProps={{
                                        readOnly: true
                                    }}
                                    className={classes.itemInput}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                                <TextField
                                    label="Bitrate detectado"
                                    value={
                                        detectedAudioInfo.bitrate ?
                                            (isValidBitrate(detectedAudioInfo.bitrate)
                                                ? `${detectedAudioInfo.bitrate}k`
                                                : 'Desconhecido (incompatível)'
                                            ) : 'Desconhecido'
                                    }
                                    size="small"
                                    variant="outlined"
                                    InputProps={{
                                        readOnly: true
                                    }}
                                    className={classes.itemInput}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                                <TextField
                                    label="Bitrate detectado (FFmpeg)"
                                    value={
                                        detectedAudioInfo.ffmpegBitrate ?
                                            (isValidBitrate(detectedAudioInfo.ffmpegBitrate)
                                                ? `${detectedAudioInfo.ffmpegBitrate}k`
                                                : 'Desconhecido (incompatível)'
                                            ) : 'Desconhecido'
                                    }
                                    size="small"
                                    variant="outlined"
                                    InputProps={{
                                        readOnly: true
                                    }}
                                    className={classes.itemInput}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                                <TextField
                                    label="Frequência detectada"
                                    value={
                                        detectedAudioInfo.frequency ?
                                            (isValidFrequency(detectedAudioInfo.frequency)
                                                ? `${detectedAudioInfo.frequency} Hz`
                                                : 'Desconhecido (incompatível)'
                                            ) : 'Desconhecido'
                                    }
                                    size="small"
                                    variant="outlined"
                                    InputProps={{
                                        readOnly: true
                                    }}
                                    className={classes.itemInput}
                                />
                            </Grid>

                        </>
                    )}
                </Grid>
                <DialogActions>
                    <Button
                        onClick={() => setOpenedAudioInfoDialog(false)}
                        color="primary"
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={applyDetectedAudioInfo}
                        color="primary"
                        variant="contained"
                    >
                        Aplicar dados compatíveis
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={openedCompanyDelete}>
                <DialogTitle className={classes.dialogTitle}>
                    Tem certeza que deseja remover <b>{companyName}</b>?
                </DialogTitle>

                <DialogActions>
                    <Button
                        id="company_delete_cancel_button"
                        onClick={() => { setOpenedCompanyDelete(false) }}
                        color="primary"
                    >
                        Cancelar
                    </Button>
                    <Button
                        id="company_delete_confirm_button"
                        onClick={() => deleteCompany(companyUuid)}
                        color="primary"
                        autoFocus
                        variant="contained"
                        endIcon={deletingCompany && (
                            <CircularProgress size={20} color="inherit" />
                        )}
                    >
                        Deletar
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={openDuplicateWarning} maxWidth="xs" fullWidth>
                <DialogTitle>Atenção</DialogTitle>
                <DialogContent>
                    <Typography gutterBottom>
                        Existem rádios com nomes parecidos na cidade <b>{company?.address?.city || '(sem cidade)'}</b>:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, my: 1.5 }}>
                        {duplicateCompany.slice(0, 8).map(d => (
                            <Chip key={d.uuid} label={d.frequency ? `${d.name} (${d.frequency})` : d.name} variant="outlined" />
                        ))}
                        {duplicateCompany.length > 8 && (
                            <Chip label={`+${duplicateCompany.length - 8} mais`} variant="outlined" sx={{ color: 'text.secondary', borderStyle: 'dashed' }} />
                        )}
                    </Box>
                    <Typography>
                        Deseja continuar mesmo assim?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDuplicateWarning(false)} color="primary">
                        Cancelar
                    </Button>
                    <Button
                        onClick={async () => {
                            ignoreDuplicateCheckRef.current = true
                            setOpenDuplicateWarning(false)
                            await saveCompany()
                            ignoreDuplicateCheckRef.current = false
                        }}
                        color="primary"
                        variant="contained"
                    >
                        Continuar mesmo assim
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={openedUsersDialog} onClose={handleCloseUsersDialog} TransitionProps={{onExited: () => setSelectedCompanyUsers([])}} maxWidth="sm" fullWidth>
                <DialogTitle style={{ textAlign: 'center' }}>Usuários</DialogTitle>
                <DialogContent style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    {selectedCompanyUsers.length === 0 ? (
                        <Typography align="center" style={{ padding: '16px 0' }}>
                            Nenhum usuário associado.
                        </Typography>
                    ) : (
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell><b>Nome</b></TableCell>
                                    <TableCell><b>E-mail</b></TableCell>
                                    <TableCell><b>Criado em</b></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {selectedCompanyUsers.map(user => (
                                    <TableRow key={user.uuid} hover>
                                        <TableCell>{user.name}</TableCell>
                                        <TableCell>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 0.5,
                                                    cursor: 'pointer',
                                                    '& .copy-icon': { opacity: 0 },
                                                    '&:hover .copy-icon': { opacity: 0.6 }
                                                }}
                                                onClick={() => {
                                                    navigator.clipboard.writeText(user.email)
                                                    setCopiedEmail(user.email)
                                                    if (timeoutRef.current) clearTimeout(timeoutRef.current)
                                                    timeoutRef.current = setTimeout(() => setCopiedEmail(null), 2000)
                                                }}
                                                title="Clique para copiar"
                                            >
                                                {user.email}
                                                {copiedEmail === user.email
                                                    ? <CheckIcon className="copy-icon" sx={{ fontSize: '14px', color: 'green', opacity: '1 !important', verticalAlign: 'middle' }} />
                                                    : <ContentCopyIcon className="copy-icon" sx={{ fontSize: '14px', verticalAlign: 'middle' }} />
                                                }
                                            </Box>
                                        </TableCell>
                                        <TableCell>{user.createdAt ? new Date(user.createdAt).toLocaleDateString('pt-BR') : '—'}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseUsersDialog} color="primary">Fechar</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={openedManageUsersDialog} onClose={handleCloseManageUsersDialog} maxWidth="sm" fullWidth>
                <DialogTitle style={{ textAlign: 'center' }}>
                    Gerenciar usuários — {manageCompanyName}
                </DialogTitle>
                <DialogContent style={{ maxHeight: '480px', overflowY: 'auto' }}>
                    <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
                        Usuários associados
                    </Typography>
                    {loadingManageUsers ? (
                        <Grid container justifyContent="center" sx={{ py: 2 }}>
                            <CircularProgress size={28} />
                        </Grid>
                    ) : manageCompanyUsers.length === 0 ? (
                        <Typography align="center" color="textSecondary" sx={{ py: 2 }}>
                            Nenhum usuário associado a esta rádio.
                        </Typography>
                    ) : (
                        <>
                            <TextField
                                id="manage_users_search_field"
                                label="Buscar usuário associado"
                                size="small"
                                variant="outlined"
                                fullWidth
                                value={manageUsersSearch}
                                onChange={({ target }) => setManageUsersSearch(target.value)}
                                sx={{ mb: 1 }}
                            />
                            {filteredManageCompanyUsers.length === 0 ? (
                                <Typography align="center" color="textSecondary" sx={{ py: 2 }}>
                                    Nenhum usuário encontrado.
                                </Typography>
                            ) : (
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell><b>Nome</b></TableCell>
                                            <TableCell><b>E-mail</b></TableCell>
                                            <TableCell align="right" />
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {filteredManageCompanyUsers.map((user) => (
                                            <TableRow key={user.uuid} hover>
                                                <TableCell>{user.name}</TableCell>
                                                <TableCell>{user.email}</TableCell>
                                                <TableCell align="right">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleRemoveUserFromCompany(user)}
                                                        disabled={removingUsers.includes(user.uuid)}
                                                        title="Desvincular da rádio"
                                                    >
                                                        {removingUsers.includes(user.uuid)
                                                            ? <CircularProgress size={18} />
                                                            : <RemoveUserIcon fontSize="small" />
                                                        }
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </>
                    )}

                    <Divider size={2} />

                    <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
                        Adicionar usuário existente
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1}>
                        <Autocomplete
                            id="manage_users_autocomplete"
                            size="small"
                            fullWidth
                            options={manageAvailableUsers}
                            getOptionLabel={(option: any) => `${option.name} (${option.email})`}
                            value={manageSelectedUser}
                            onChange={(_, value) => setManageSelectedUser(value)}
                            loading={loadingManageUsers}
                            renderInput={(params) => (
                                <TextField {...params} label="Buscar usuário" />
                            )}
                        />
                        <Button
                            variant="contained"
                            color="primary"
                            disabled={!manageSelectedUser || savingManageUser === manageSelectedUser?.uuid}
                            onClick={handleAddUserToCompany}
                            endIcon={savingManageUser === manageSelectedUser?.uuid && (
                                <CircularProgress size={16} color="inherit" />
                            )}
                        >
                            Adicionar
                        </Button>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseManageUsersDialog} color="primary">Fechar</Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default Companies
